import React from 'react'
import { Box, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ResumeButton } from './ResumeButton'

interface NavigationItem {
    key: string
    path: string
    label: string
    isResume?: boolean
}

interface NavigationButtonsProps {
    variant: 'full' | 'simplified'
    showAuthLinks: boolean
    user: { id: string } | null
    navigationItems: NavigationItem[]
    authItems: NavigationItem[]
    isActive: (path: string) => boolean
    isLessonPage: () => boolean
    onNavigation: (path: string) => void
    onResumeClick: () => void
    mostRecentStory: { storyId: string; storyTitle: string } | null
    progressLoading: boolean
    isLlmLocked?: boolean
}

export const NavigationButtons: React.FC<NavigationButtonsProps> = ({
    variant,
    showAuthLinks,
    user,
    navigationItems,
    authItems,
    isActive,
    isLessonPage,
    onNavigation,
    onResumeClick,
    mostRecentStory,
    progressLoading,
    isLlmLocked = false,
}) => {
    const { t } = useTranslation()
    if (variant === 'simplified' && showAuthLinks) {
        return (
            <Box sx={{ display: 'flex', gap: 1 }}>
                {authItems.map((item) => (
                    <Button
                        key={item.key}
                        color="inherit"
                        onClick={() => onNavigation(item.path)}
                        sx={{
                            fontWeight: isActive(item.path) ? 600 : 400,
                            backgroundColor: isActive(item.path)
                                ? 'action.selected'
                                : 'transparent',
                        }}
                        aria-current={isActive(item.path) ? 'page' : undefined}
                        aria-label={t(`navigation:${item.key}`)}
                    >
                        {item.label}
                    </Button>
                ))}
            </Box>
        )
    }

    if (!user) {
        return (
            <Box sx={{ display: 'flex', gap: 1 }}>
                {authItems.map((item) => (
                    <Button
                        key={item.key}
                        color="inherit"
                        onClick={() => onNavigation(item.path)}
                        sx={{
                            fontWeight: isActive(item.path) ? 600 : 400,
                            backgroundColor: isActive(item.path)
                                ? 'action.selected'
                                : 'transparent',
                        }}
                        aria-current={isActive(item.path) ? 'page' : undefined}
                        aria-label={t(`navigation:${item.key}`)}
                    >
                        {item.label}
                    </Button>
                ))}
            </Box>
        )
    }

    // Simplified variant for authenticated users (e.g. lesson pages):
    // show only non-resume navigation items to reduce distraction
    if (variant === 'simplified') {
        return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                {navigationItems
                    .filter((item) => !item.isResume)
                    .map((item) => (
                        <Button
                            key={item.key}
                            color="inherit"
                            onClick={() => onNavigation(item.path)}
                            sx={{
                                fontWeight: 400,
                                backgroundColor: isActive(item.path)
                                    ? 'action.selected'
                                    : 'transparent',
                            }}
                            aria-current={isActive(item.path) ? 'page' : undefined}
                            aria-label={t(`navigation:${item.key}`)}
                        >
                            {item.label}
                        </Button>
                    ))}
            </Box>
        )
    }

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {navigationItems.map((item) => {
                if (item.isResume) {
                    return (
                        <ResumeButton
                            key={item.key}
                            mostRecentStory={mostRecentStory}
                            progressLoading={progressLoading}
                            isLessonPage={isLessonPage()}
                            onClick={onResumeClick}
                            llmLocked={isLlmLocked}
                        />
                    )
                }

                return (
                    <Button
                        key={item.key}
                        color="inherit"
                        onClick={() => onNavigation(item.path)}
                        sx={{
                            fontWeight: 400,
                            backgroundColor: isActive(item.path)
                                ? 'action.selected'
                                : 'transparent',
                        }}
                        aria-current={isActive(item.path) ? 'page' : undefined}
                        aria-label={t(`navigation:${item.key}`)}
                    >
                        {item.label}
                    </Button>
                )
            })}
        </Box>
    )
}
